/**
 * Decrypts PII values returned by the backend's `encrypt_pii` helper.
 *
 * Backend format (see pay_options_middleware/middleware/utils/encryption.py):
 *   - key  = SHA-256(PAY_OPTIONS_PII_ENCRYPTION_KEY)
 *   - cipher = AES-256-CBC with PKCS7 padding
 *   - wire = base64(IV[16 bytes] || ciphertext)
 *
 * The shared secret is read from VUE_APP_PII_ENCRYPTION_KEY and must match
 * the backend setting exactly. If the key is missing or decryption fails for
 * any reason, the input is returned unchanged so the UI degrades gracefully
 * (the alternative — empty strings — silently hides the customer's identity).
 */

import { ISuperPacmanInstallment } from "@/types/ISuperPacman";

const IV_LENGTH_BYTES = 16;

let cachedKeyPromise: Promise<CryptoKey | null> | null = null;
let cachedKeyMaterial: string | null = null;

function getKeyMaterial(): string {
  return process.env.VUE_APP_PII_ENCRYPTION_KEY ?? "";
}

function hasWebCrypto(): boolean {
  return typeof globalThis.crypto?.subtle !== "undefined" && typeof globalThis.TextEncoder !== "undefined";
}

async function deriveKey(): Promise<CryptoKey | null> {
  const passphrase = getKeyMaterial();
  if (!passphrase) return null;
  if (!hasWebCrypto()) return null;
  if (cachedKeyPromise && cachedKeyMaterial === passphrase) return cachedKeyPromise;

  cachedKeyMaterial = passphrase;
  cachedKeyPromise = (async () => {
    const hashed = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(passphrase));
    return crypto.subtle.importKey("raw", hashed, { name: "AES-CBC" }, false, ["decrypt"]);
  })();
  return cachedKeyPromise;
}

function base64ToBytes(value: string): Uint8Array | null {
  try {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Decrypts a single base64 IV+ciphertext value. Returns the input unchanged
 * when the value is empty, the key is unavailable, or decryption fails.
 */
export async function decryptPii(value: string | null | undefined): Promise<string> {
  if (!value) return "";

  try {
    const key = await deriveKey();
    if (!key) return value;

    const raw = base64ToBytes(value);
    if (!raw || raw.length <= IV_LENGTH_BYTES) return value;

    const iv = raw.slice(0, IV_LENGTH_BYTES);
    const ciphertext = raw.slice(IV_LENGTH_BYTES);
    const plaintext = await crypto.subtle.decrypt({ name: "AES-CBC", iv }, key, ciphertext);
    return new TextDecoder("utf-8").decode(plaintext);
  } catch (error) {
    console.warn("PII decryption failed, returning original value", error);
    return value;
  }
}

/**
 * Decrypts the PII fields the backend encrypts on the SuperPacman
 * installments response, mutating the passed object in place.
 */
export async function decryptSuperPacmanFinanceProductPii(
  installment: ISuperPacmanInstallment | null | undefined,
): Promise<void> {
  if (!installment?.financeProduct) return;

  const fp = installment.financeProduct;
  const [name, phone, email, nationalId, dateOfBirth, firstName, lastName, zipCode, deviceImei] = await Promise.all([
    decryptPii(fp.customerName),
    decryptPii(fp.customerPhoneNumber),
    decryptPii(fp.customerEmail),
    decryptPii(fp.customerNationalIdNumber),
    decryptPii(fp.customerDateOfBirth),
    decryptPii(fp.customerFirstName),
    decryptPii(fp.customerLastName),
    decryptPii(fp.customerZipCode),
    decryptPii(fp.deviceImei),
  ]);

  fp.customerName = name;
  fp.customerPhoneNumber = phone;
  fp.customerEmail = email;
  fp.customerNationalIdNumber = nationalId;
  fp.customerDateOfBirth = dateOfBirth;
  fp.customerFirstName = firstName;
  fp.customerLastName = lastName;
  fp.customerZipCode = zipCode;
  fp.deviceImei = deviceImei;
}

// Visible for testing — clears the cached key derivation when the env changes
// between tests.
export function __resetPiiKeyCacheForTests(): void {
  cachedKeyPromise = null;
  cachedKeyMaterial = null;
}
